import { useState, useCallback, useRef } from 'react';
import useBank from './useBank';
import { BET_DENOMINATIONS } from '../utils/GameConstants';

const REEL_COUNT = 3;
const SYMBOLS_PER_REEL = 5;
const REEL_LENGTH = 20;

const symbols = ['cherry', 'lemon', 'apple', 'gem', 'bell', 'star', 'seven'];
const symbolWeights = [35, 30, 25, 20, 10, 5, 2];
const payouts = {
  'cherry': 2,
  'lemon': 3,
  'apple': 4,
  'gem': 6,
  'bell': 10,
  'star': 15,
  'seven': 20
};

const getRandomSymbol = () => {
  const totalWeight = symbolWeights.reduce((sum, weight) => sum + weight, 0);
  let random = Math.random() * totalWeight;
  for (let i = 0; i < symbols.length; i++) {
    if (random < symbolWeights[i]) {
      return symbols[i];
    }
    random -= symbolWeights[i];
  }
  return symbols[symbols.length - 1];
};

const generateReel = () => {
  return Array.from({ length: REEL_LENGTH }, () => getRandomSymbol());
};

export const useSlotMachine = () => {
  const { user, depositMoney, withdrawMoney, refreshUser } = useBank();
  const [reels, setReels] = useState(Array(REEL_COUNT).fill().map(() => Array(SYMBOLS_PER_REEL).fill('cherry')));
  const [spinning, setSpinning] = useState(false);
  const [betAmount, setBetAmount] = useState(0);
  const [winAmount, setWinAmount] = useState(0);
  const [message, setMessage] = useState('');
  const [reelPositions, setReelPositions] = useState([0, 0, 0]);
  const fullReels = useRef(Array(REEL_COUNT).fill().map(generateReel));

  const betDenominations = BET_DENOMINATIONS;

  const checkWin = useCallback((finalReels) => {
    const middleRow = finalReels.map(reel => reel[2]);
    console.log('Middle row:', middleRow);
    
    if (middleRow.every(symbol => symbol === middleRow[0])) {
      const winAmount = betAmount * payouts[middleRow[0]];
      console.log(`Win! Symbol: ${middleRow[0]}, Payout: ${payouts[middleRow[0]]}, Win Amount: ${winAmount}`);
      return winAmount;
    }
    
    if (middleRow[0] === middleRow[1] || middleRow[1] === middleRow[2]) {
      const matchingSymbol = middleRow[0] === middleRow[1] ? middleRow[0] : middleRow[1];
      const winAmount = Math.floor(betAmount * payouts[matchingSymbol] / 2);
      console.log(`Partial win! Symbol: ${matchingSymbol}, Win Amount: ${winAmount}`);
      return winAmount;
    }
    
    console.log('No win');
    return 0;
  }, [betAmount]);

  const finalizeSpin = useCallback(async (finalPositions) => {
    const visibleSymbols = finalPositions.map((pos, reelIndex) => 
      [
        fullReels.current[reelIndex][(pos - 2 + REEL_LENGTH) % REEL_LENGTH],
        fullReels.current[reelIndex][(pos - 1 + REEL_LENGTH) % REEL_LENGTH],
        fullReels.current[reelIndex][pos],
        fullReels.current[reelIndex][(pos + 1) % REEL_LENGTH],
        fullReels.current[reelIndex][(pos + 2) % REEL_LENGTH]
      ]
    );
    console.log('Finalizing spin with reels:', visibleSymbols);
    const win = checkWin(visibleSymbols);
    if (win > 0) {
      await withdrawMoney(win);
      setWinAmount(win);
      setMessage(`You won 💴${win}!`);
    } else {
      setWinAmount(0);
      setMessage('Try again!');
    }
    setSpinning(false);
    setReels(visibleSymbols);
    refreshUser();
  }, [checkWin, withdrawMoney, refreshUser]);

  const spin = useCallback(async () => {
    if (!user) {
      setMessage('User data not available. Please try again.');
      return;
    }

    if (user.money >= betAmount && !spinning && betAmount > 0) {
      setSpinning(true);
      setMessage('');
      await depositMoney(betAmount);

      const newPositions = reelPositions.map(() => Math.floor(Math.random() * REEL_LENGTH));
      setReelPositions(newPositions);

      const spinDuration = 2000;
      const intervalDuration = 50;
      const totalSteps = spinDuration / intervalDuration;
      let currentStep = 0;

      const spinInterval = setInterval(() => {
        if (currentStep < totalSteps) {
          setReelPositions(prevPositions => 
            prevPositions.map((pos) => (pos + 1) % REEL_LENGTH)
          );
          setReels(prevReels => 
            prevReels.map(() => 
              Array(SYMBOLS_PER_REEL).fill().map(() => getRandomSymbol())
            )
          );
          currentStep++;
        } else {
          clearInterval(spinInterval);
          finalizeSpin(newPositions);
        }
      }, intervalDuration);

    } else if (spinning) {
      setMessage('Already spinning!');
    } else if (betAmount <= 0) {
      setMessage('Please place a bet.');
    } else {
      setMessage('Insufficient funds to place the bet.');
    }
  }, [user, betAmount, spinning, depositMoney, reelPositions, finalizeSpin]);

  const handleBet = useCallback((amount) => {
    if (!spinning) {
      setBetAmount(prevBet => prevBet + amount);
    }
  }, [spinning]);

  const clearBet = useCallback(() => {
    if (!spinning) {
      setBetAmount(0);
    }
  }, [spinning]);

  return {
    user,
    reels,
    spinning,
    betAmount,
    winAmount,
    message,
    spin,
    handleBet,
    clearBet,
    betDenominations,
  };
};